import Vue from "vue";
import axios from "axios";
import router from "@/router";
import store from "@/store/index.js";
import QS from "qs";
import {Loading} from "element-ui";
import {showSingleMessage} from "@/utils/messageOnce";

export const antiCrawlerBus = new Vue();

// http 状态码
const ERROR_CODE = {
    400: "错误请求",
    401: "会话过期",
    403: "拒绝访问",
    404: "请求错误,未找到该资源",
    405: "请求方法未允许",
    408: "请求超时",
    500: "网络连接到服务器失败",
    501: "网络未实现",
    502: "网络错误",
    503: "服务不可用",
    504: "网络超时",
    505: "http版本不支持该请求",
};
// 请求超时时间
const AXIOSTIME = 30 * 1000;

const startLoading = () => {
    return Loading.service({
        lock: true,
        text: "加载中……",
        target: document.body,
        background: "rgba(0, 0, 0, 0.5)",
    });
};
const Instance = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL,
    timeout: AXIOSTIME,
});
Instance.defaults.timeout = AXIOSTIME;
// 设备凭证通过 X-Anti-Crawler-Device-Id 请求头传递，浏览器不再处理 Cookie。
Instance.defaults.withCredentials = false;
Instance.defaults.crossDomain = false;

const DEVICE_CACHE_KEY = "ANTI_CRAWLER_DEVICE_ID";

function readDeviceId() {
  try {
    return window.localStorage.getItem(DEVICE_CACHE_KEY);
  } catch (error) {
    return null;
  }
}

function saveDeviceId(response) {
  const headerValue = response && response.headers && (
    response.headers["x-anti-crawler-device-id"] ||
    response.headers["X-Anti-Crawler-Device-Id"]
  );
  const bodyValue = response && response.data && response.data.result &&
    response.data.result.deviceId;
  const deviceId = headerValue || bodyValue;
  if (deviceId) {
    try {
      window.localStorage.setItem(DEVICE_CACHE_KEY, deviceId);
    } catch (error) {
      // 本地缓存不可用时仍保留当前请求链路。
    }
  }
}
Instance.defaults.headers.post["Content-Type"] =
    "application/json;charset=utf-8";
Instance.defaults.headers.put["Content-Type"] =
    "application/json;charset=utf-8";

// request 拦截器
Instance.interceptors.request.use(
    (config) => {
        const token = Vue.ls.get("ACCESS_TOKEN");

        config.headers = {
            "Content-Type": "application/json;charset=utf-8",
            Accept: "*/*",
            ...config.headers,
        };
        const deviceId = readDeviceId();
        if (deviceId) {
            config.headers["X-Anti-Crawler-Device-Id"] = deviceId;
        }
        const isFormData = config.data instanceof FormData;
        if (isFormData) {
            // FormData 上传：删除 Content-Type 让浏览器自动设置 multipart boundary
            delete config.headers["Content-Type"];
        }
        if (token && !config.skipIpTokenRefresh) {
            config.headers["X-Access-Token"] = token;
        } else if (config.skipIpTokenRefresh) {
            delete config.headers["X-Access-Token"];
        }
        // FormData 由浏览器自动生成 multipart boundary，不能 JSON.stringify。
        // 否则会被序列化成 "{}" 字符串，导致后端收到的是 application/json 空 body，
        // 触发 CommonController.upload 的 (MultipartHttpServletRequest) request 强转失败。
        if (isFormData) {
            return config;
        }
        if (config.data && typeof config.data !== "string") {
            config.data = JSON.stringify(config.data);
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

let antiCrawlerPromise = null;
let ipAutoLoginPromise = null;
let redirectingToLogin = false;

function redirectToLogin(error) {
  const requestHeaders = (error && error.config && error.config.headers) || {};
  const hadAccessToken = Boolean(
    requestHeaders["X-Access-Token"] || requestHeaders["x-access-token"]
  );
  const responseData = error && error.response && error.response.data;
  const backendMessage = typeof responseData === "string"
    ? responseData
    : responseData && (responseData.message || responseData.msg);
  if (hadAccessToken) {
    showSingleMessage({
      message: backendMessage || "Token 已过期，请重新登录",
      type: "info",
    });
  }
  Vue.ls.remove("ACCESS_TOKEN");
  store.commit("permission/ResetPermission");
  store.dispatch("app/resetAfterLogout");
  if (redirectingToLogin || window.location.pathname === "/login") {
    return;
  }

  redirectingToLogin = true;
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const redirect = currentPath || router.currentRoute.fullPath || "/home";
  router.replace({
    path: "/login",
    query: { redirect },
  }).catch(() => {});
}

async function hasAntiCrawlerChallenge(error) {
  const response = error && error.response;
  if (!response || response.status !== 429) {
    return false;
  }
  const headers = response.headers || {};
  const challengeHeader = headers["x-anti-crawler-challenge"] ||
    headers["X-Anti-Crawler-Challenge"];
  let data = response.data || {};
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      const text = await data.text();
      data = text ? JSON.parse(text) : {};
      response.data = data;
    } catch (parseError) {
      data = {};
    }
  }
  const message = typeof data === "string" ? data : data.message || data.msg || "";
  return Boolean(
    challengeHeader ||
    (typeof data === "object" && data.antiCrawlerChallenge === true) ||
    message.indexOf("需要完成安全验证") > -1
  );
}

function waitForIpAutoLogin() {
  if (!ipAutoLoginPromise) {
    ipAutoLoginPromise = store.dispatch("user/ipAutoLogin").finally(() => {
      ipAutoLoginPromise = null;
    });
  }
  return ipAutoLoginPromise;
}

function waitForAntiCrawlerChallenge(error) {
  if (!antiCrawlerPromise) {
    antiCrawlerPromise = new Promise((resolve, reject) => {
      antiCrawlerBus.$emit("anti-crawler-challenge", {
        challengeId: null,
        resolve,
        reject,
        sourceError: error,
      });
    }).finally(() => {
      antiCrawlerPromise = null;
    });
  }
  return antiCrawlerPromise;
}

// response 拦截器
Instance.interceptors.response.use(
  (response) => {
    saveDeviceId(response);
    const { code, message } = response.data || {};
    if (code === 500) {
      showSingleMessage({
        message,
        type: "error",
      });
      return Promise.reject("error");
    }
    return response;
  },
  async (error) => {
        if (error && error.response) {
            const status = error.response.status;
      if (status === 401) {
        const config = error.config || {};
        if (config._ipAutoLoginRequest || ipAutoLoginPromise) {
          return Promise.reject(error);
        }
        const requestHeaders = config.headers || {};
        const hadAccessToken = Boolean(
          requestHeaders["X-Access-Token"] || requestHeaders["x-access-token"]
        );
        const canRefreshToken =
          process.env.VUE_APP_IP_AUTO_LOGIN === "true" &&
          hadAccessToken &&
          !config._ipAutoLoginRequest &&
          !config._ipTokenRetried &&
          !ipAutoLoginPromise;

        if (canRefreshToken) {
          // Token 失效后只自动刷新一次，成功后重试原请求。
          const refreshed = await waitForIpAutoLogin();
          if (refreshed) {
            showSingleMessage({
              message: "自动登录成功",
              type: "success",
            });
            const retryConfig = {
              ...config,
              headers: {
                ...(config.headers || {}),
                "X-Access-Token": Vue.ls.get("ACCESS_TOKEN"),
              },
              _ipTokenRetried: true,
            };
            return Instance.request(retryConfig);
          }
        }

        redirectToLogin(error);
        return Promise.reject(error);
      }
      if (await hasAntiCrawlerChallenge(error) && error.config && !error.config._antiCrawlerRetried) {
        const challengeResult = await waitForAntiCrawlerChallenge(error);
        if (typeof challengeResult !== "string" || !challengeResult) {
          return Promise.reject(error);
        }
        const retryConfig = {
          ...error.config,
          headers: {
            ...(error.config.headers || {}),
            "X-Anti-Crawler-Challenge-Token": challengeResult,
          },
          _antiCrawlerRetried: true,
        };
        delete retryConfig._retry;
        return Instance.request(retryConfig);
      }
            switch (status) {
        case 500:
                    showSingleMessage({
                        message: error.response.data.message,
                        type: "warning",
                    });
                    break;
                default: {
                    const responseData = error.response.data || {};
                    const responseMessage = typeof responseData === "string"
                        ? responseData
                        : responseData.message || responseData.msg;
                    error.message = responseMessage || (ERROR_CODE.hasOwnProperty(status)
                        ? ERROR_CODE[status]
                        : `连接错误${status}`);
                    showSingleMessage({
                        message: error.message,
                        type: "warning",
                    });
                }
            }
        } else {
            showSingleMessage({
                message: "网络连接到服务器超时，请联系管理员",
                type: "warning",
            });
        }
        return Promise.reject(error);
    }
);

const $https = {};
const methods = ["get", "post", "patch", "put", "delete"];
for (let i of methods) {
    $https[i] = function (url, params = {}, responseType, loading = false) {
        return new Promise((resolve, reject) => {
            const previousResponseType = Instance.defaults.responseType;
            if (responseType) {
                Instance.defaults.responseType = responseType;
            }
            let $loading = null;
            loading && ($loading = startLoading());
            // get 请求，参数序列化
            if (i == "get") {
                const pm = QS.stringify(params);
                Instance.get(`${url}?${pm}`)
                    .then((res) => {
                        loading && $loading.close();
                        Instance.defaults.responseType = previousResponseType;
                        resolve(res.data);
                    })
                    .catch((err) => {
                        reject(err);
                        $loading && $loading.close();
                        Instance.defaults.responseType = previousResponseType;
                    });
            } else {
                Instance[i](url, params)
                    .then((res) => {
                        loading && $loading.close();
                        Instance.defaults.responseType = previousResponseType;
                        resolve(res.data);
                    })
                    .catch((err) => {
                        reject(err);
                        $loading && $loading.close();
                        Instance.defaults.responseType = previousResponseType;
                    });
            }
        });
    };
}

// GET请求返回Blob（用于PDF等二进制文件）
$https.getBlob = function(url, params = {}) {
    return new Promise((resolve, reject) => {
        const pm = QS.stringify(params);
        Instance.get(`${url}?${pm}`, { responseType: 'blob' })
            .then((res) => {
                resolve(res.data);
            })
            .catch((err) => {
                reject(err);
            });
    });
}

// GET Blob，同时返回完整 Axios Response
// 专门给 PDF Range 请求使用
$https.getBlobResponse = function(
  url,
  params = {},
  config = {}
) {
  const pm = QS.stringify(params)

  return Instance.get(
    `${url}?${pm}`,
    {
      ...config,
      responseType: 'blob'
    }
  )
}
export {Instance};

export default $https;
